import { DASH, EVENT_TONES } from './dashboardTokens';

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Bežný tím má do 15 ľudí — pod touto hranicou je useknutý zoznam horší než
// žiadny („a ďalší traja" nepovie nič). Riadky sú preto stiahnuté tak, aby sa
// pätnástka zmestila bez toho, aby tooltip prerástol obrazovku.
export const MAX_TOOLTIP_ROWS = 15;

// Do ôsmich mien ostáva zoznam jednostĺpcový a úzky.
const SINGLE_COLUMN_ROWS = 8;

export const tooltipPalette = (theme) => ({
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    line: theme.palette.divider,
});

/**
 * Obsah tooltipu grafu dochádzky: nadpis, jednoriadkový súhrn a menný rozpis.
 *
 * Stĺpec sám povie „koľko“; hodnotu pridáva až to, kto to bol a o koľkej — preto
 * je zoznam mien hlavným telom tooltipu a nie prívesok pod číslom.
 *
 * @param {{title: string, summary: string, rows: Array<{name: string, detail: string, live?: boolean}>,
 *          moreLabel?: string, palette: object}} params
 */
export const renderAttendanceTooltip = ({ title, summary, rows, moreLabel, footnote, palette }) => {
    const renderRow = ({ name, detail, live, warn }) => {
        const dot = (color) => `<span style="width:6px;height:6px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0"></span>`;
        return `
        <div style="display:flex;align-items:baseline;justify-content:space-between;gap:16px;line-height:19px">
            <span style="color:${palette.text};font-size:12.5px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(name)}</span>
            <span style="color:${warn ? EVENT_TONES.break_start : palette.muted};font-size:12.5px;font-variant-numeric:tabular-nums;white-space:nowrap;display:flex;align-items:center;gap:5px;flex-shrink:0">
                ${live ? dot(EVENT_TONES.arrival) : ''}${warn ? dot(EVENT_TONES.break_start) : ''}${escapeHtml(detail)}
            </span>
        </div>
    `;
    };

    // Nad osem mien sa zoznam láme do dvoch stĺpcov — inak by bol tooltip vyšší
    // než graf, nad ktorým visí. Číta sa zhora nadol, potom vpravo.
    const split = rows.length > SINGLE_COLUMN_ROWS;
    const half = Math.ceil(rows.length / 2);
    const column = (items) => `<div style="flex:1;min-width:206px">${items.map(renderRow).join('')}</div>`;
    const body = split
        ? `<div style="display:flex;gap:24px">${column(rows.slice(0, half))}${column(rows.slice(half))}</div>`
        : column(rows);

    return `
        <div style="padding:11px 14px 12px;font-family:inherit">
            <div style="font-family:${DASH.display};font-size:13.5px;font-weight:600;letter-spacing:-0.01em;color:${palette.text}">${escapeHtml(title)}</div>
            <div style="font-size:11.5px;color:${palette.muted};margin-top:2px">${escapeHtml(summary)}</div>
            ${rows.length ? `<div style="height:1px;background:${palette.line};margin:9px 0 6px"></div>${body}` : ''}
            ${moreLabel ? `<div style="font-size:11.5px;color:${palette.muted};margin-top:7px">${escapeHtml(moreLabel)}</div>` : ''}
            ${footnote ? `
                <div style="height:1px;background:${palette.line};margin:8px 0 7px"></div>
                <div style="display:flex;align-items:center;gap:6px;font-size:11.5px;color:${palette.muted}">
                    <span style="width:6px;height:6px;border-radius:50%;background:${EVENT_TONES.break_start};display:inline-block;flex-shrink:0"></span>
                    ${escapeHtml(footnote)}
                </div>` : ''}
        </div>
    `;
};

/** Zjednotí vzhľad rámu tooltipu s panelmi prehľadu. */
export const tooltipContainerSx = (theme) => ({
    '& .apexcharts-tooltip': {
        borderRadius: `${DASH.radiusSm}px`,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        boxShadow: theme.palette.mode === 'dark'
            ? '0 12px 32px rgba(0,0,0,0.55)'
            : '0 12px 32px rgba(15,23,42,0.14)',
        overflow: 'hidden',
    },
    '& .apexcharts-tooltip.apexcharts-theme-light, & .apexcharts-tooltip.apexcharts-theme-dark': {
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
    },
});
